import React, {useCallback, useEffect, useState} from 'react';
import {Alert, Button, Col, Row, Spin, Tag, Typography} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import AccountDetailsCard from '../AccountDetailsCard';
import OrderList from '../../pages/trading/components/OrderList';
import PocTradingFormCard from './PocTradingFormCard';
import {useAuth} from '../../contexts/AuthContext';
import {useI18n} from '../../i18n/useI18n';
import {apiConnectionService} from '../../services/apiConnectionService';
import './PocTradingView.css';

const {Title, Text} = Typography;

const PocTradingView: React.FC = () => {
    const {t} = useI18n();
    const {isAuthenticated} = useAuth();
    const [globalApiKeyId, setGlobalApiKeyId] = useState<number | null>(null);
    const [globalApiKeyName, setGlobalApiKeyName] = useState<string | null>(null);
    const [globalExchange, setGlobalExchange] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchConnection = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await apiConnectionService.getGlobalDisplayConnection();
            if (result?.apiKeyId) {
                setGlobalApiKeyId(result.apiKeyId);
                setGlobalApiKeyName(result.keyName ?? null);
                setGlobalExchange(result.exchange ?? null);
            } else {
                setError('site.pocTradingView.noConnection');
            }
        } catch {
            setError('site.pocTradingView.loadError');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchConnection();
    }, [fetchConnection]);

    const handleNoop = () => {
    };

    if (loading) {
        return (
            <div className="poc-trading-view"
                 style={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400}}>
                <Spin size="large"/>
            </div>
        );
    }

    return (
        <div className="poc-trading-view">
            <div className="poc-trading-view-header">
                <div>
                    <Title level={4} className="poc-trading-view-title">
                        <InfoCircleOutlined style={{marginRight: 8, color: 'var(--gold)'}}/>
                        {t('site.pocTradingView.title')}
                    </Title>
                    <Text className="poc-trading-view-subtitle">
                        {t('site.pocTradingView.subtitle')}
                    </Text>
                </div>
            </div>

            {!isAuthenticated && globalExchange && (
                <div className="poc-trading-view-identity">
                    <Tag color="gold">{globalExchange.toUpperCase()}</Tag>
                </div>
            )}

            {error && (
                <Alert
                    message={t('site.pocTradingView.errorTitle')}
                    description={t(error)}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setError(null)}
                    action={
                        <Button size="small" onClick={fetchConnection}>
                            {t('site.pocTradingView.retry')}
                        </Button>
                    }
                    style={{marginBottom: 12}}
                />
            )}

            <Alert
                className="poc-trading-view-notice"
                description={t('site.pocTradingView.notice')}
                type="info"
                showIcon
                icon={<InfoCircleOutlined/>}
            />

            <div className="poc-trading-view-content">
                <div className="poc-trading-view-scaled">
                    <Row gutter={[16, 16]}>
                        <Col xs={24} lg={15}>
                            <div className="poc-account-section">
                                <AccountDetailsCard
                                    apiKeyId={globalApiKeyId}
                                    refreshInterval={10000}
                                    autoRefresh={true}
                                    title="账户详情"
                                />
                            </div>
                            <div className="poc-form-section">
                                <PocTradingFormCard apiKeyId={globalApiKeyId}/>
                            </div>
                        </Col>
                        <Col xs={24} lg={9}>
                            <div className="poc-orderlist-section">
                                <OrderList
                                    apiKeyId={globalApiKeyId}
                                    onOrderUpdate={handleNoop}
                                    readOnly={true}
                                />
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    );
};

export default PocTradingView;
